import * as Headless from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Button } from '@oysterjs/ui/button';
import React from 'react';
import { type WorkspacePdfFile } from './artifact-focus-html-document-render-pdf-attachment';
import { PdfAttachmentOptions } from './pdf-attachment-options';

export const MENU_PANEL_CLASS =
  'absolute right-0 z-[100] -mr-1 mt-2 w-[min(100vw-2rem,28rem)] max-h-[min(90vh,40rem)] overflow-y-auto origin-top-right rounded-md bg-white dark:bg-neutral-800 shadow-lg ring-1 ring-black/5 dark:ring-white/10 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in';

type OpenPdfWithAttachmentsButtonProps = {
  onOpenPdf: () => void;
  isOpening: boolean;
  openError: string | null;
  openNotice?: string | null;
  disabled?: boolean;
  chevronAriaLabel?: string;
  attachmentCatalog: WorkspacePdfFile[];
  prefixFileIds: string[];
  suffixFileIds: string[];
  onAttachmentChange: (prefixFileIds: string[], suffixFileIds: string[]) => void;
  onLocalFilesChange?: (localFiles: Map<string, File>) => void;
  /** Extra sections rendered above PDF attachments in the chevron panel. */
  children?: React.ReactNode;
  attachmentsDescription?: string;
};

export const OpenPdfWithAttachmentsButton: React.FC<OpenPdfWithAttachmentsButtonProps> = ({
  onOpenPdf,
  isOpening,
  openError,
  openNotice,
  disabled = false,
  chevronAriaLabel = 'More PDF options',
  attachmentCatalog,
  prefixFileIds,
  suffixFileIds,
  onAttachmentChange,
  onLocalFilesChange,
  children,
  attachmentsDescription = 'Prepend pages before or append pages after the generated document (this export only)'
}) => (
  <div className="inline-block">
    <div className="inline-flex rounded-lg shadow-sm">
      <Button
        color="sky"
        onClick={onOpenPdf}
        disabled={isOpening || disabled}
        className="rounded-l-lg rounded-r-none border-r-0 before:rounded-l-[calc(theme(borderRadius.lg)-1px)] before:rounded-r-none after:rounded-l-[calc(theme(borderRadius.lg)-1px)] after:rounded-r-none dark:after:rounded-l-lg dark:after:rounded-r-none"
      >
        {isOpening ? 'Opening…' : 'Open PDF'}
      </Button>
      <Headless.Popover className="relative -ml-px block">
        <Headless.PopoverButton
          as={Button}
          color="sky"
          className="rounded-r-lg rounded-l-none border-l-0 before:rounded-r-[calc(theme(borderRadius.lg)-1px)] before:rounded-l-none after:rounded-r-[calc(theme(borderRadius.lg)-1px)] after:rounded-l-none dark:after:rounded-r-lg dark:after:rounded-l-none"
          aria-label={chevronAriaLabel}
        >
          <ChevronDownIcon aria-hidden="true" className="size-5" />
        </Headless.PopoverButton>
        <Headless.PopoverPanel transition anchor="bottom end" className={MENU_PANEL_CLASS}>
          <PdfAttachmentOptions
            description={attachmentsDescription}
            attachmentCatalog={attachmentCatalog}
            prefixFileIds={prefixFileIds}
            suffixFileIds={suffixFileIds}
            onChange={onAttachmentChange}
            onLocalFilesChange={onLocalFilesChange}
          >
            {children}
          </PdfAttachmentOptions>
        </Headless.PopoverPanel>
      </Headless.Popover>
    </div>
    {openError && (
      <div className="mt-1.5 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-3 py-2 text-xs text-red-600 dark:text-red-400">
        {openError}
      </div>
    )}
    {openNotice && (
      <div role="status" className="mt-1.5 text-xs text-neutral-600 dark:text-neutral-300">
        {openNotice}
      </div>
    )}
  </div>
);
